// Problem-focused landing pages: Losing Clients, Losing Staff, Competing on Price

export const PROBLEM_PAGES = {
  'improve-public-awareness': {
    slug: 'improve-public-awareness',
    navLabel: 'Public Awareness',
    eyebrow: 'For organisations doing great work no one hears about',
    headline: 'Let the public understand just how special you are.',
    yellowWord: 'special',
    intro: "If you feel the public don't fully understand or appreciate just how special the work you're doing really is, Yellow can fix that problem.",
    heroImage: 'https://customer-assets-m6fa6gv7.emergentagent.net/job_f12b922e-b1fd-440c-9793-474906f03870/artifacts/x1mol0xq_video%20production%20liverpool%20-%20presenting%20to%20camera.jpg',
    symptoms: {
      title: 'The signs your public profile is holding you back',
      items: [
        "You're doing incredible work but the public and press don't know about it",
        'Investors, funders or commissioners are asking hard questions about impact and value for money',
        'Attendance, uptake or usage of your programmes, services or launches is lower than the effort behind them',
        'People assume you\'re just another faceless organisation, not the specialists you actually are',
        'Great human stories are sitting untold inside your team',
        'Your CSR, community and impact work goes almost invisible outside your walls'
      ]
    },
    approachTitle: 'How Yellow fixes it',
    approachSubtitle: 'Innovative digital, social and awareness campaigns that show the difference you make to peoples lives.',
    approach: [
      {
        step: '01',
        title: 'Understand what makes you special',
        body: 'We start by getting to know story and people behind your organisation. What sets you apart, who benefits, and why the story matters.'
      },
      {
        step: '02',
        title: 'Find the human stories worth telling',
        body: 'The strongest public awareness campaigns are led by real people. We identify the staff, service users, students, patients or community voices whose stories will do more for engagement than any statistic on its own.'
      },
      {
        step: '03',
        title: 'Produce emotional, inspiring content',
        body: 'From documentary-style films and social-first video to campaign websites, design, print and out-of-home, we produce work that proves your impact and makes people feel it, not just read about it.'
      },
      {
        step: '04',
        title: 'Launch, measure and prove the results',
        body: 'We roll the campaign out across the right channels for your audience and give you the reach, engagement and sentiment reporting you need to prove the money was spent well, to the public, to investors, and to funders.'
      }
    ],
    deliverables: {
      title: 'What you can get',
      items: [
        'A public awareness campaign that finally shows people how special your work really is',
        'Documentary-style video content that captures real staff, service user and community voices',
        'Social-first cuts and digital assets built for LinkedIn, Facebook, Instagram and TikTok',
        'Campaign microsites and landing pages where the story lives in one place',
        'Print, out-of-home and event materials that stand up next to your existing brand guidelines',
        'Case study films you can share with investors, funders, commissioners and press',
        'A steady flow of content so your programme keeps living long beyond launch day',
        'Reach, engagement and sentiment reporting you can put in front of any board or funder',
        'Innovative, creative campaigns built from strategy, video, design and web all under one roof'
      ]
    },
    supportModes: {
      title: 'Choose the support that suits you...',
      subtitle: 'Either way, the outcome is the same, more people see, understand and value the work you do.',
      modes: [
        {
          name: 'Implementation',
          label: 'Full campaign delivery',
          image: 'https://customer-assets-m6fa6gv7.emergentagent.net/job_f12b922e-b1fd-440c-9793-474906f03870/artifacts/ybn3eikh_video%20production%20liverpool%20-%20shaun.jpg',
          imageAlt: 'The Yellow team on set producing an awareness campaign, marketing agency Liverpool',
          summary: "Yellow becomes your outsourced campaign team. We plan, produce and run the whole awareness programme (strategy, film, design, web and social) while your team stays focused on the day job.",
          items: [
            'End-to-end awareness campaign delivered under one roof',
            'Documentary-style films that capture real staff, service user and community voices',
            'Campaign microsite, landing pages and digital ads built to convert attention into action',
            'Social-first cuts, print and out-of-home materials rolled out across every channel',
            'Reach, engagement and sentiment reporting you can put in front of boards, investors or funders'
          ]
        },
        {
          name: 'Strategy & Training',
          label: 'Coaching your in-house team',
          image: 'https://customer-assets-m6fa6gv7.emergentagent.net/job_f12b922e-b1fd-440c-9793-474906f03870/artifacts/riyrorpb_A001_03262245_C150.braw.22_45_46_14.Still002.webp',
          imageAlt: 'Yellow leading an awareness campaign workshop, marketing agency Liverpool mentorship & training',
          summary: "Yellow builds the strategy and coaches your team so you can run the awareness campaign in-house, drawing on our experience of over 1,000 successful campaigns.",
          items: [
            'A clear, board-ready awareness strategy tailored to your organisation and audience',
            'Practical workshops for your marketing, comms and PR teams',
            'A campaign playbook so your team knows exactly what to produce, when and where',
            'Feedback loops on the content, films and campaigns your team is already producing',
            'Ongoing mentorship for comms leads on storytelling, positioning and measurable impact'
          ]
        }
      ]
    },
    ctaHeadline: 'Show people how special the work you do really is.',
    ctaBody: "Let's turn your impact into a campaign the public actually sees, understands and remembers."
  },

  'losing-staff': {
    slug: 'losing-staff',
    navLabel: 'Better Internal Comms',
    eyebrow: 'For businesses losing staff',
    headline: 'Revolutionise how you inspire, retain and train your staff.',
    yellowWord: 'inspire, retain and train',
    intro: "Replacing a staff member can cost 6 to 9 months of their salary, and every departure knocks culture and client relationships. Yellow builds the story, comms and culture campaigns that makes people want to join, believe in your future, and stay.",
    heroImage: 'https://customer-assets-m6fa6gv7.emergentagent.net/job_f12b922e-b1fd-440c-9793-474906f03870/artifacts/0iy8e9fr_DAVE%20HEADER%20CROPPED.jpg',
    symptoms: {
      title: 'The signs your team is at risk',
      items: [
        "Good people are leaving within their first 12 months",
        'New starters don\u2019t seem to "get" the business until month six',
        'Your values live on a poster nobody reads',
        'Nobody outside the leadership team can explain the 3-year vision',
        'You have great CSR stories, but nobody knows about them',
        'Job ads attract volume, not the right people'
      ]
    },
    approachTitle: 'How Yellow fixes it',
    approachSubtitle: 'A programme that makes your business easier to join, easier to believe in, and much harder to leave.',
    approach: [
      {
        step: '01',
        title: 'Onboarding that lands, from day one',
        body: 'We turn your onboarding into a story. A mix of welcome videos, culture films, first-week guides and manager scripts. So every new starter understands who you are, what you stand for and how they fit in.'
      },
      {
        step: '02',
        title: 'Values everyone actually believes',
        body: 'We help you define, film and communicate your values so they show up in real behaviour, not just a wall in reception. Short internal videos, town-hall slides, and shareable social clips turn values into a language your team uses every day.'
      },
      {
        step: '03',
        title: 'Sell them the future',
        body: "We build a compelling 'where we're heading' narrative your leadership can use in every all-hands, 1:1 and interview, so people feel excited to still be here in 3 years, not FOMO about their mate's new job."
      },
      {
        step: '04',
        title: 'Grow your CSR & community story',
        body: "We film, write and promote the good things you're already doing (community projects, charity partnerships, sustainability wins, apprenticeship schemes) so employees are proud to say where they work, and candidates want in."
      }
    ],
    deliverables: {
      title: 'What you get',
      items: [
        'An employer brand your team is proud to represent',
        'An onboarding process that lands from day one',
        'New starters who understand your values and vision within their first week',
        'Existing staff who can articulate the 3-year plan back to you',
        'A CSR and community story that makes people want to join',
        'A regular internal comms rhythm that keeps everyone in the loop',
        'A recruitment funnel that attracts the right people, not just volume',
        'Lower staff churn and the huge cost that comes with it',
        'A team that feels connected to something bigger than the day job',
        'Innovative, creative content provided consistently to followers and clients'
      ]
    },
    supportModes: {
      title: 'Choose the support that suits your team...',
      subtitle: 'Either way, the outcome is the same, your people feel connected, proud and motivated to stay.',
      modes: [
        {
          name: 'Implementation',
          label: 'Full campaign delivery',
          image: 'https://customer-assets-m6fa6gv7.emergentagent.net/job_f12b922e-b1fd-440c-9793-474906f03870/artifacts/ybn3eikh_video%20production%20liverpool%20-%20shaun.jpg',
          imageAlt: 'The Yellow team producing an internal comms campaign, marketing agency Liverpool',
          summary: "Yellow becomes your outsourced internal comms team. We plan, produce and roll out the whole employer brand and internal communications programme so your HR and leadership teams can stay focused on the day job.",
          items: [
            'End-to-end internal comms and employer brand campaign delivered under one roof',
            'Onboarding films, values videos and staff story content produced on location',
            'Regular internal comms content, newsletters, town halls and social-first cuts',
            'Recruitment-ready campaign assets to attract the right people, not just volume',
            'Reach, engagement and sentiment reporting so leadership can see the impact'
          ]
        },
        {
          name: 'Strategy & Training',
          label: 'Coaching your in-house team',
          image: 'https://customer-assets-m6fa6gv7.emergentagent.net/job_f12b922e-b1fd-440c-9793-474906f03870/artifacts/riyrorpb_A001_03262245_C150.braw.22_45_46_14.Still002.webp',
          imageAlt: 'Yellow leading an internal comms workshop, marketing agency Liverpool mentorship & training',
          summary: "Yellow builds the strategy and coaches your HR, comms and leadership teams so you can run the internal comms programme in-house, drawing on our experience of over 1,000 successful campaigns.",
          items: [
            'A board-ready employer brand and internal comms strategy tailored to your organisation',
            'Practical workshops for your HR, comms and leadership teams',
            'A playbook so your team knows exactly what to produce, when and where',
            'Feedback loops on the content and campaigns your team is already producing',
            'Ongoing mentorship on storytelling, values comms and measurable retention impact'
          ]
        }
      ]
    },
    proofStat: '6\u20139mo',
    proofLabel: '',
    proofExplanation: 'Losing an experienced employee can cost a business the equivalent of six to nine months of their salary once recruitment, training, lost productivity and disruption are included.',
    ctaHeadline: 'Great teams don\u2019t leave. They compound.',
    ctaBody: "Let us help you build the story that makes people want to stay."
  },

  'competing-on-price': {
    slug: 'competing-on-price',
    navLabel: 'Competing On Price?',
    eyebrow: "For businesses undercut by cheaper competitors",
    headline: 'Are you sick of competing on price?',
    yellowWord: 'price',
    intro: "Only the biggest and cheapest win on price. Yellow builds the credibility and thought-leadership that makes buyers trust you as the expert, not the cheap option they'll regret. Whether you want us to run the whole programme in-house for you or coach your own team to do it, we help you stop trading margin for market share.",
    heroImage: 'https://customer-assets-m6fa6gv7.emergentagent.net/job_f12b922e-b1fd-440c-9793-474906f03870/artifacts/dgl0m41k_event%20pic%20cropped%20less.jpg',
    symptoms: {
      title: 'The signs you\u2019re trapped in a price war',
      items: [
        'Prospects ask for discounts on every single proposal',
        'You\u2019re losing pitches to cheaper, lower-quality competitors',
        'Your sales team spends more time justifying price than selling value',
        'You have no visible authority. No talks, PR, podcasts or thought-leadership',
        'Website testimonials all say "great value" instead of "expert" or "trusted"',
        'Referrals still ask "how much?" before they ask "how do we start?"'
      ]
    },
    approachTitle: 'How Yellow fixes it',
    approachSubtitle: 'A programme that repositions you as the obvious expert and makes the cheaper option feel like the risky option, delivered fully by our team or taught to yours.',
    approach: [
      {
        step: '01',
        title: 'Reposition around expertise, not price',
        body: 'We rewrite your positioning, website and sales copy so every touch-point reinforces that you\u2019re the specialist (the safe, sensible, expert choice), not just another supplier for buyers to compare on a spreadsheet.'
      },
      {
        step: '02',
        title: 'Build visible authority',
        body: 'We produce thought-leadership content (short videos, podcasts, articles, LinkedIn posts) that puts your best minds in front of your ideal buyers every week, until you\u2019re the name they think of first.'
      },
      {
        step: '03',
        title: 'Educate buyers on real value',
        body: "Cheap options exist because buyers don\u2019t know what to look for. We build the guides, comparison content and 'buy cheap, buy twice' stories that help prospects understand exactly what they\u2019re risking by picking the low-cost quote."
      },
      {
        step: '04',
        title: 'Weaponise trust & proof',
        body: 'Case studies, on-camera testimonials, awards, accreditations, ratings and results, all packaged and used at every stage of the buying journey so by the time you pitch, the sale is already largely done.'
      }
    ],
    supportModes: {
      title: 'Choose The Support That Suits Your Goals...',
      subtitle: 'Both routes lead to the same place. You stop competing on price.',
      modes: [
        {
          name: 'Done For You',
          label: 'Full-service delivery',
          image: 'https://customer-assets-m6fa6gv7.emergentagent.net/job_f12b922e-b1fd-440c-9793-474906f03870/artifacts/ybn3eikh_video%20production%20liverpool%20-%20shaun.jpg',
          imageAlt: 'The Yellow team on set reviewing footage, marketing agency Liverpool',
          summary: "Yellow becomes your remote marketing team. We plan, create and run everything for you (strategy, video, social, website, design & branding) while your team stays focused on the day job.",
          items: [
            'Positioning and messaging rewritten for you so you stop being compared on price alone',
            'Strategy, video, photography, design, website and social all produced under one roof',
            'A public reputation as the expert in your field, built by our team, week in, week out',
            'Innovative, creative content delivered consistently to your followers and clients',
            'A visible wall of proof: case studies, on-camera testimonials, awards and results, all packaged by us'
          ]
        },
        {
          name: 'Mentorship & Training',
          label: 'Coaching your in-house team',
          image: 'https://customer-assets-m6fa6gv7.emergentagent.net/job_f12b922e-b1fd-440c-9793-474906f03870/artifacts/riyrorpb_A001_03262245_C150.braw.22_45_46_14.Still002.webp',
          imageAlt: 'Yellow leading a strategy workshop, marketing agency Liverpool mentorship & training',
          summary: "Yellow mentors leaders and teams so you can take your destiny into your own hands, drawing on our experience of over 1,000 successful marketing projects. Our experts provide the strategy and training to help you meet your goals.",
          items: [
            '1:1 mentorship for founders, MDs and marketing leads on positioning and pricing confidence',
            'Practical workshops for your marketing, sales and comms teams',
            'A clear playbook so your team knows exactly what to produce, and when',
            'Feedback loops on the content, campaigns and pitches your team is already producing',
            'Training on how to educate buyers on real value, not just talk about features'
          ]
        }
      ]
    },
    proofStat: '',
    proofLabel: '',
    ctaHeadline: 'Stop discounting. Start commanding your price.',
    ctaBody: "Tell us where you're at and we'll come back with a proposal: done-for-you, mentorship, or a mix of both."
  },

  'public-sector': {
    slug: 'public-sector',
    navLabel: 'Public Sector Campaigns',
    eyebrow: 'For the public sector',
    headline: 'Struggling to get the public to see the work you actually do?',
    yellowWord: 'work',
    intro: "Councils, forces, trusts and departments do life-changing work every day, but if the public doesn't see it, funding gets questioned and initiatives quietly disappear. Yellow shows off what you're spending money on with campaigns, digital content and emotional, inspiring video so communities understand, engage with, and support the programmes you run.",
    heroImage: 'https://customer-assets-m6fa6gv7.emergentagent.net/job_f12b922e-b1fd-440c-9793-474906f03870/artifacts/x1mol0xq_video%20production%20liverpool%20-%20presenting%20to%20camera.jpg',
    symptoms: {
      title: 'The signs your programme isn\u2019t landing',
      items: [
        'The scheme is delivering results but the public and press don\u2019t know about it',
        'Attendance at your events and initiatives is lower than the budget behind them',
        'Councillors, commissioners or trustees are asking hard questions about value for money',
        'Your comms team is stretched and there\u2019s no time to produce the content the story deserves',
        'Case studies and human stories are sitting untold in your team\u2019s inbox',
        'You need to demonstrate impact to secure the next round of funding'
      ]
    },
    approachTitle: 'How Yellow fixes it',
    approachSubtitle: 'A campaign, content and film programme built for the realities of public sector comms. Procurement-ready, sensitive to community, and designed to move people.',
    approach: [
      {
        step: '01',
        title: 'Understand the initiative and the audience',
        body: 'We start with a proper briefing: what the programme does, who it\u2019s for, and what \u201Cgood\u201D looks like to the residents, partners and funders you need to reach. No jargon, just clarity.'
      },
      {
        step: '02',
        title: 'Find the human stories worth telling',
        body: 'The strongest public sector campaigns are led by real people. We identify the frontline workers, service users and community voices whose stories will do more for engagement than any statistic on its own.'
      },
      {
        step: '03',
        title: 'Produce emotional, inspiring content',
        body: 'From short social films and radio-ready audio to full documentary-style video, printed materials and digital campaigns, we produce work that shows off the impact of your programme without ever feeling like a party political broadcast.'
      },
      {
        step: '04',
        title: 'Launch, measure and report on impact',
        body: 'We build the campaign roll-out with your comms team, target the right channels, and give you the reach, engagement and sentiment reporting you need to justify the spend and prove the results.'
      }
    ],
    deliverables: {
      title: 'What you get',
      items: [
        'A public-facing campaign that makes your initiative feel visible, valuable and worth funding',
        'Documentary-style video content that captures real staff and community voices',
        'Social-first cuts and digital assets built for LinkedIn, Facebook, Instagram and TikTok',
        'Print, out-of-home and event materials that stand up next to your existing brand guidelines',
        'Case study films you can share with commissioners, councillors and central government',
        'A steady stream of content so your programme keeps living beyond launch day',
        'Sensitivity and safeguarding built into every step of production',
        'Clear reach, engagement and sentiment reporting for your next funding review',
        'A team that has delivered for the NHS, Merseyside & Metropolitan Police, and city councils including Sheffield and Liverpool'
      ]
    },
    supportModes: {
      title: 'Choose the support that suits your team...',
      subtitle: 'Either way, the outcome is the same, the public understands the good work you are doing and your funders see it too.',
      modes: [
        {
          name: 'Implementation',
          label: 'Full campaign delivery',
          image: 'https://customer-assets-m6fa6gv7.emergentagent.net/job_f12b922e-b1fd-440c-9793-474906f03870/artifacts/ybn3eikh_video%20production%20liverpool%20-%20shaun.jpg',
          imageAlt: 'The Yellow team producing a public sector campaign, marketing agency Liverpool',
          summary: "Yellow becomes your outsourced campaign team. We plan, produce and roll out the whole public awareness programme, strategy, film, design, web and social, while your comms team stays focused on their day job.",
          items: [
            'End-to-end public sector campaign delivered under one roof',
            'Documentary-style films capturing frontline staff, service users and community voices',
            'Campaign microsite, landing pages and digital ads built to convert attention into action',
            'Social-first cuts, print and out-of-home materials rolled out across every channel',
            'Sensitivity, safeguarding and stakeholder sign-off built into every step',
            'Reach, engagement and sentiment reporting you can put in front of commissioners, councillors and funders'
          ]
        },
        {
          name: 'Strategy & Training',
          label: 'Coaching your in-house team',
          image: 'https://customer-assets-m6fa6gv7.emergentagent.net/job_f12b922e-b1fd-440c-9793-474906f03870/artifacts/riyrorpb_A001_03262245_C150.braw.22_45_46_14.Still002.webp',
          imageAlt: 'Yellow leading a public sector marketing workshop, marketing agency Liverpool mentorship & training',
          summary: "Yellow builds the strategy and coaches your in-house comms team so you can run the campaign yourselves, drawing on our experience of over 1,000 successful public and private sector campaigns.",
          items: [
            'A board-ready public awareness strategy tailored to your programme and audience',
            'Practical workshops for your comms, PR and marketing teams',
            'A campaign playbook so your team knows exactly what to produce, when and where',
            'Feedback loops on the films, content and campaigns your team is already producing',
            'Ongoing mentorship for comms leads on storytelling, positioning and measurable impact'
          ]
        }
      ]
    },
    ctaHeadline: 'Show the public what their money is really doing.',
    ctaBody: "Let\u2019s turn your programme's impact into content the community actually sees, understands and supports."
  }
};

export const PROBLEM_SEO = {
  'improve-public-awareness': {
    title: 'Improve Public Awareness, Marketing Agency Liverpool | Yellow',
    description: 'Public awareness campaigns for private companies, universities, hospitals, PCNs and public sector. Yellow builds strategy, film, design and web that prove your impact.'
  },
  'losing-staff': {
    title: 'Better Internal Comms, Marketing Agency Liverpool | Yellow',
    description: 'Better internal comms. Yellow, a marketing agency Liverpool, helps you communicate values, onboarding and future ambitions so your team stays engaged and stays put.'
  },
  'competing-on-price': {
    title: 'For Businesses Competing on Price, Marketing Agency Liverpool | Yellow',
    description: 'Stop competing on price. Yellow, a marketing agency Liverpool, builds brand-led marketing that wins on value, justify premium pricing and stop the race to the bottom.'
  },
  'public-sector': {
    title: 'Public Sector Campaigns, Marketing Agency Liverpool | Yellow',
    description: 'Public sector campaigns for councils, NHS trusts, PCNs, forces and government departments. Yellow builds strategy, film and content that shows communities the good work you actually do.'
  }
};
